import { Component, EventEmitter, OnInit, OnDestroy, Output, NgZone } from '@angular/core';
import { trigger, style, animate, transition, AnimationEvent } from '@angular/animations';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ResultService } from 'src/app/services/result.service';
import { TranslateHandler } from 'src/app/common/helpers/translate-handler';
import { environment } from 'src/environments/environment';
import { Router, NavigationEnd } from '@angular/router';
import { PageKeyword } from '../enums/page-keyword.enum';
import { filter } from 'rxjs/operators';
import { Subscription, Observable } from 'rxjs';

interface SearchSession {
  question?: string;
  answer?: string;
  articleList?: any[];
  askErrorText?: string;
  askErrorDescription?: string;
  selectedArticleSlug?: string | null;
  contactPopupVisible?: boolean;
  contactMessage?: string;
  pageKeyword?: string | null;
  savedAt?: number;
  hasSearchSession?: boolean;
}

@Component({
    selector: 'app-support-popup-main',
    templateUrl: './support-popup-main.component.html',
    styleUrls: ['./support-popup-main.component.scss'],
    animations: [
        trigger('slideInOut', [
            transition(':enter', [
                style({ transform: 'translateX(100%)', opacity: 0 }),
                animate('500ms cubic-bezier(0.25, 0.8, 0.25, 1)', style({ transform: 'translateX(0)', opacity: 1 })),
            ]),
            transition(':leave', [
                style({ transform: 'translateX(0)', opacity: 1, position: 'fixed', right: 0 }),
                animate('500ms cubic-bezier(0.25, 0.8, 0.25, 1)', style({ transform: 'translateX(100%)', opacity: 0 })),
            ]),
        ]),
    ],
    standalone: false
})
export class SupportPopupMainComponent implements OnInit, OnDestroy {

  private readonly SEARCH_DATA_SESSION_KEY = 'supportPopupData';
  private readonly SEARCH_TEXT_KEY = 'searchText';
  private readonly ARTICLES_CACHE_SESSION_KEY = 'supportArticlesCache';
  private readonly LOADING_STATE_KEY = 'supportPopupLoadingState';
  private readonly REQUEST_ACTIVE_KEY = 'supportPopupRequestActive';
  private readonly MSG_VALIDATION = 'support_popup.lbl_txt_validation_message';
  private readonly MSG_ERROR_TEXT = 'support_popup.lbl_ask_error_text';
  private readonly MSG_ERROR_DESC = 'support_popup.lbl_ask_error_description';

  private readonly CONTACT_MESSAGE_KEY = 'contactMessage';
  private readonly UNLOAD_TS_KEY = 'supportPopupLastUnload';

  articleUrl = environment.SUPPORT_ARTICLE_URL;
  @Output() closed = new EventEmitter<void>();
  animationState: 'in' | 'out' = 'in';
  searchForm: UntypedFormGroup = new UntypedFormGroup({
    searchText: new UntypedFormControl('')
  });
  isErrorVisible = false;
  txtValidationMsg = '';
  answer = '';
  askErrorText = '';
  askErrorDescription = '';
  articleList: any[] = [];
  isLoading = false;
  showArticlePopup = false;
  selectedArticleSlug: string | undefined;
  isVisible = true;
  isAutoLoadedArticles = false;
  isContactUsPopupVisible = false;
  activeTab: 'help' | 'support' = 'help';
  hasActivatedSupportTab = false;
  openNewCaseRequestId: number | null = null;
  supportCasePrefillMessage = '';
  currentPageKeyword: string | null = null;

  private articleCache: Record<string, any[]> = {};
  private currentArticlesRequest?: Subscription;
  private currentAskRequest?: Subscription;
  private routerSubscription?: Subscription;

  private hasSearchSession = false;
  private hasPageSelection = false;
  isClosing = false;

  constructor(
    private resultService: ResultService,
    private translateHandler: TranslateHandler,
    private router: Router,
    private zone: NgZone
  ) { }

  ngOnInit(): void {
    this.currentPageKeyword = this.getKeywordForCurrentPage();
    this.handlePageRefresh();
    this.loadArticlesCacheFromSession();
    this.restoreSearchSessionOrPageArticles();
    this.listenRouterChanges();
    window.addEventListener('beforeunload', this.onBeforeUnload);
  }

  private handlePageRefresh(): void {
    const navEntries = performance.getEntriesByType("navigation") as PerformanceNavigationTiming[];
    const isRefresh = navEntries.length > 0 && navEntries[0].type === "reload";
  
    if (isRefresh) {
      this.clearSession(this.SEARCH_DATA_SESSION_KEY);
      sessionStorage.removeItem(this.CONTACT_MESSAGE_KEY);
    }
  }

  private restoreSearchSessionOrPageArticles(): void {
    const data = this.loadFromSession<SearchSession>(this.SEARCH_DATA_SESSION_KEY);

    this.restoreSearchTextOnly();

    const requestActive =
      sessionStorage.getItem(this.REQUEST_ACTIVE_KEY) === 'true' ||
      sessionStorage.getItem(this.LOADING_STATE_KEY) === 'loading';

    if (requestActive) {
      this.hasSearchSession = true;
      this.isLoading = true;
      return;
    }

    if (!data) {
      this.hasSearchSession = false;
      this.loadArticlesForCurrentPage();
      return;
    }

    this.hasSearchSession = !!data.hasSearchSession;

    if (this.hasSearchSession) {
      this.restoreSearchSession(data);
      return;
    }

    const currentKeyword = this.getKeywordForCurrentPage();
    if (
      data.selectedArticleSlug &&
      data.pageKeyword &&
      currentKeyword &&
      data.pageKeyword === currentKeyword
    ) {
      this.articleList = data.articleList || [];
      this.selectedArticleSlug = data.selectedArticleSlug || undefined;
      this.isAutoLoadedArticles = true;
      this.isLoading = false;
      return;
    }

    this.selectedArticleSlug = undefined;
    this.loadArticlesForCurrentPage();
  }

  private onBeforeUnload = () => {
    try {
      sessionStorage.setItem(this.UNLOAD_TS_KEY, Date.now().toString());
    } catch (e) { /* ignore */ }
  };

  private restoreSearchSession(data: SearchSession): void {
    this.searchForm.get('searchText')?.setValue(data.question || '');
    this.answer = data.answer || '';
    this.articleList = data.articleList || [];
    this.askErrorText = data.askErrorText || '';
    this.askErrorDescription = data.askErrorDescription || '';
    this.selectedArticleSlug = data.selectedArticleSlug || undefined;
    this.isContactUsPopupVisible = !!data.contactPopupVisible;
  
    this.isLoading = false;
    this.isAutoLoadedArticles = false;
  }

  private restorePageArticles(data: SearchSession): void {
    const currentKeyword = this.getKeywordForCurrentPage();

    const pageMatch =
      data.pageKeyword &&
      currentKeyword &&
      data.pageKeyword === currentKeyword;

    if (pageMatch && data.selectedArticleSlug) {
      this.articleList = data.articleList || [];
      this.selectedArticleSlug = data.selectedArticleSlug || undefined;
      this.isAutoLoadedArticles = true;
      this.isLoading = false;
    } else {
      this.selectedArticleSlug = undefined;
      this.clearPageSelectionFromSession();
      this.loadArticlesForCurrentPage();
    }
  }

  private clearPageSelectionFromSession(): void {
    const data = this.loadFromSession<SearchSession>(this.SEARCH_DATA_SESSION_KEY);
    if (!data) return;
    data.selectedArticleSlug = null;
    data.pageKeyword = null;
    this.saveToSession(this.SEARCH_DATA_SESSION_KEY, data);
    this.hasPageSelection = false;
  }

  private listenRouterChanges(): void {
    this.routerSubscription = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => {
        this.currentPageKeyword = this.getKeywordForCurrentPage();
        this.currentArticlesRequest?.unsubscribe();

        if (this.hasSearchSession) {
          return;
        }

        this.selectedArticleSlug = undefined;
        this.answer = '';
        this.articleList = [];
        this.askErrorText = '';
        this.askErrorDescription = '';
        this.hasPageSelection = false;

        this.clearPageSelectionFromSession();
        this.loadArticlesForCurrentPage();
      });
  }

  public getAskResults(question: string): void {
    const trimmed = question?.trim();
    if (!trimmed) {
      this.isErrorVisible = true;
      this.txtValidationMsg = this.translateHandler.translate(this.MSG_VALIDATION);
      return;
    }

    this.hasSearchSession = true;
    this.hasPageSelection = false;

    sessionStorage.setItem(this.REQUEST_ACTIVE_KEY, 'true');
    sessionStorage.setItem(this.LOADING_STATE_KEY, 'loading');

    const earlySession: SearchSession = {
      hasSearchSession: true,
      question: trimmed,
      pageKeyword: this.getKeywordForCurrentPage(),
      savedAt: Date.now()
    };
    this.saveToSession(this.SEARCH_DATA_SESSION_KEY, earlySession);

    this.currentArticlesRequest?.unsubscribe();
    this.currentAskRequest?.unsubscribe();

    this.resetState(false);

    this.currentAskRequest = this.resultService.getAskResult(trimmed).subscribe({
      next: (result) => this.handleAskResultResponse(result),
      error: () => this.handleAskResultError()
    });
  }

  private loadArticlesForCurrentPage(): void {
    const keyword = this.getKeywordForCurrentPage();
    if (!keyword) return;

    const cacheHit = this.articleCache[keyword];
    if (cacheHit && cacheHit.length > 0) {
      this.resetState(true);
      this.articleList = cacheHit.slice();
      this.isLoading = false;
      this.isAutoLoadedArticles = true;
      return;
    }

    this.currentArticlesRequest?.unsubscribe();
    this.resetState(true);
    this.currentArticlesRequest = this.resultService.getAskResult(keyword)
      .subscribe({
        next: (result) => {
          const list = result?.listOfArticles || [];
          this.articleList = list;
          this.isLoading = false;
          this.isAutoLoadedArticles = true;

          this.articleCache[keyword] = list;
          this.saveCacheToSession();
        },
        error: () => {
          this.isLoading = false;
          this.askErrorText = this.translateHandler.translate(this.MSG_ERROR_TEXT);
          this.askErrorDescription = this.translateHandler.translate(this.MSG_ERROR_DESC);
        }
      });
  }

  private resetState(isAutoLoad: boolean): void {
    this.isErrorVisible = false;
    this.askErrorText = '';
    this.askErrorDescription = '';
    this.isLoading = true;
    this.answer = '';
    this.articleList = [];
    this.isAutoLoadedArticles = isAutoLoad;
  }

  private handleAskResultResponse(result: any, saveSession = true): void {
    sessionStorage.setItem(this.LOADING_STATE_KEY, 'done');
    sessionStorage.setItem(this.REQUEST_ACTIVE_KEY, 'false');

    this.answer = result?.answer || '';
    this.articleList = result?.listOfArticles || [];
    this.isLoading = false;

    this.hasSearchSession = true;
    this.hasPageSelection = false;

    if (saveSession) {
      const data: SearchSession = {
        hasSearchSession: true,
        question: this.searchForm.get('searchText')?.value || '',
        answer: this.answer,
        articleList: this.articleList,
        askErrorText: this.askErrorText,
        askErrorDescription: this.askErrorDescription,
        selectedArticleSlug: this.selectedArticleSlug || null,
        contactPopupVisible: this.isContactUsPopupVisible,
        contactMessage: sessionStorage.getItem(this.CONTACT_MESSAGE_KEY) || '',
        pageKeyword: this.getKeywordForCurrentPage(),
        savedAt: Date.now()
      };
      this.saveToSession(this.SEARCH_DATA_SESSION_KEY, data);
    }
  }

  private handleAskResultError(saveSession = true): void {
    sessionStorage.setItem(this.LOADING_STATE_KEY, 'done');
    sessionStorage.setItem(this.REQUEST_ACTIVE_KEY, 'false');

    this.isLoading = false;
    this.askErrorText = this.translateHandler.translate(this.MSG_ERROR_TEXT);
    this.askErrorDescription = this.translateHandler.translate(this.MSG_ERROR_DESC);

    if (saveSession) {
      const data: SearchSession = {
        question: this.searchForm.get('searchText')?.value || '',
        answer: this.answer,
        articleList: this.articleList,
        askErrorText: this.askErrorText,
        askErrorDescription: this.askErrorDescription,
        selectedArticleSlug: this.selectedArticleSlug || null,
        contactPopupVisible: this.isContactUsPopupVisible,
        contactMessage: sessionStorage.getItem(this.CONTACT_MESSAGE_KEY) || '',
        pageKeyword: this.getKeywordForCurrentPage(),
        savedAt: Date.now()
      };
      this.saveToSession(this.SEARCH_DATA_SESSION_KEY, data);
    }
  }

  private saveToSession<T>(key: string, data: T): void {
    try {
      sessionStorage.setItem(key, JSON.stringify(data));
    } catch (e) {
      console.error(`Error saving to session storage for key "${key}":`, e);
    }
  }
  private saveCurrentSearchText(): void {
    const currentQuestion = this.searchForm.get('searchText')?.value || '';
    this.saveToSession(this.SEARCH_TEXT_KEY, currentQuestion);
  }

  private loadFromSession<T>(key: string): T | null {
    try {
      const json = sessionStorage.getItem(key);
      return json ? JSON.parse(json) as T : null;
    } catch (e) {
      console.error(`Error loading from session storage for key "${key}":`, e);
      return null;
    }
  }

  private restoreSearchTextOnly(): void {
    const data = this.loadFromSession<SearchSession>(this.SEARCH_TEXT_KEY);
    if (data) {
      this.searchForm.get('searchText')?.setValue(data);
    }
  }
  private clearSession(key: string): void {
    try {
      sessionStorage.removeItem(key);
    } catch (e) { 
      console.error(`Error clearing session storage for key "${key}":`, e);
    }
  }

  private saveCacheToSession(): void {
    try {
      sessionStorage.setItem(this.ARTICLES_CACHE_SESSION_KEY, JSON.stringify(this.articleCache));
    } catch (e) {
      console.error('Error saving article cache to session:', e);
    }
  }

  private loadArticlesCacheFromSession(): void {
    try {
      const json = sessionStorage.getItem(this.ARTICLES_CACHE_SESSION_KEY);
      this.articleCache = json ? JSON.parse(json) : {};
    } catch (e) {
      console.error('Error loading article cache from session:', e);
      this.articleCache = {};
    }
  }

  public getKeywordForCurrentPage(): string | null {
    const currentUrl = this.router.url;
    for (const key in PageKeyword) {
      if (currentUrl.includes(key)) {
        const translationKey = PageKeyword[key as keyof typeof PageKeyword];
        return this.translateHandler.translate(translationKey);
      }
    }
    return null;
  }

  public convertToHtml(text: string): string {
    return text ? text.replace(/\n/g, '<br/>') : '';
  }

  public openArticle(slug: string): void {
    this.selectedArticleSlug = slug;
    if (!this.hasSearchSession) {
      this.hasPageSelection = true;
    }

    const data: SearchSession = {
      hasSearchSession: this.hasSearchSession,
      question: this.searchForm.get('searchText')?.value || '',
      answer: this.answer,
      articleList: this.articleList,
      selectedArticleSlug: slug,
      pageKeyword: this.getKeywordForCurrentPage(),
      savedAt: Date.now()
    };

    this.saveToSession(this.SEARCH_DATA_SESSION_KEY, data);
  }


  public closePopup(): void {
    this.selectedArticleSlug = undefined;
    this.hasPageSelection = false;
    const data = this.loadFromSession<SearchSession>(this.SEARCH_DATA_SESSION_KEY) || {};
    data.selectedArticleSlug = null;
    data.contactPopupVisible = this.isContactUsPopupVisible || false;
    data.contactMessage = sessionStorage.getItem(this.CONTACT_MESSAGE_KEY) || '';
    this.saveToSession(this.SEARCH_DATA_SESSION_KEY, data);
  }

  public setActiveTab(tab: 'help' | 'support'): void {
    this.activeTab = tab;
    if (tab === 'support') {
      this.hasActivatedSupportTab = true;
    }
    if (tab !== 'help') {
      this.selectedArticleSlug = undefined;
      this.isContactUsPopupVisible = false;

      const data = this.loadFromSession<SearchSession>(this.SEARCH_DATA_SESSION_KEY);
      if (data) {
        data.selectedArticleSlug = null;
        data.contactPopupVisible = false;
        this.saveToSession(this.SEARCH_DATA_SESSION_KEY, data);
      }
    }
  }

  public close(): void {
    this.saveCurrentSearchText();
    this.isVisible = false;
  }

  public onAnimationDone(event: AnimationEvent): void {
    if (event.toState === 'void') {
      this.closed.emit();
    }
  }

  contactSupport(): void {
    this.supportCasePrefillMessage = this.searchForm.get('searchText')?.value || '';
    this.openNewCaseRequestId = (this.openNewCaseRequestId || 0) + 1;
    this.setActiveTab('support');
  }

  openContactUsPopup() {
    this.isContactUsPopupVisible = true;
    const data = this.loadFromSession<SearchSession>(this.SEARCH_DATA_SESSION_KEY) || {};
    data.contactPopupVisible = true;
    data.contactMessage = sessionStorage.getItem(this.CONTACT_MESSAGE_KEY) || '';
    data.question = this.searchForm.get('searchText')?.value || data.question || '';
    this.saveToSession(this.SEARCH_DATA_SESSION_KEY, data);
  }

  closeContactUsPopup() {
    this.isContactUsPopupVisible = false;
    const data = this.loadFromSession<SearchSession>(this.SEARCH_DATA_SESSION_KEY) || {};
    data.contactPopupVisible = false;
    data.contactMessage = sessionStorage.getItem(this.CONTACT_MESSAGE_KEY) || '';
    this.saveToSession(this.SEARCH_DATA_SESSION_KEY, data);
  }

  clearSearch() {
    this.searchForm.get('searchText')?.setValue('');
    this.answer = '';
    this.articleList = [];
    this.askErrorText = '';
    this.askErrorDescription = '';
    this.selectedArticleSlug = undefined;
    this.hasSearchSession = false;
    this.hasPageSelection = false;
    this.clearSession(this.SEARCH_DATA_SESSION_KEY);
    this.loadArticlesForCurrentPage();
  }
  
  ngOnDestroy(): void {
    window.removeEventListener('beforeunload', this.onBeforeUnload);
    this.routerSubscription?.unsubscribe();
    this.currentAskRequest?.unsubscribe();
    this.currentArticlesRequest?.unsubscribe();
  }
}